import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { validateResetToken, resetPassword } from '../services/api';

function ResetPasswordForm() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);
  const [username, setUsername] = useState('');

  useEffect(() => {
    // Validate token on component mount
    const checkToken = async () => {
      setIsValidating(true);

      try {
        const result = await validateResetToken(token);
        setIsValidating(false);

        if (result.error) {
          setError(result.error);
          setIsValidToken(false);
        } else {
          setIsValidToken(true);
          setUsername(result.username);
        }
      } catch (err) {
        setIsValidating(false);
        setError('Failed to validate reset link');
        setIsValidToken(false);
      }
    };

    if (token) {
      checkToken();
    } else {
      setIsValidating(false);
      setError('Invalid reset link');
      setIsValidToken(false);
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!newPassword || !confirmPassword) {
      setError('Please enter and confirm your new password');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setIsLoading(true);

    try {
      const result = await resetPassword(token, newPassword);
      setIsLoading(false);

      if (result.error) {
        setError(result.error);
      } else {
        // Success - redirect to login with success message
        navigate('/login', {
          state: {
            message: 'Password successfully reset. Please log in with your new password.'
          }
        });
      }
    } catch (err) {
      setIsLoading(false);
      setError('An unexpected error occurred. Please try again.');
    }
  };

  if (isValidating) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="w-full max-w-sm bg-surface rounded-lg shadow-lg p-6 space-y-5 border border-muted text-center">
          <div className="text-brand-white">Validating reset link...</div>
        </div>
      </div>
    );
  }

  if (!isValidToken) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="w-full max-w-sm bg-surface rounded-lg shadow-lg p-6 space-y-5 border border-muted">
          <h2 className="text-2xl font-bold text-brand-white text-center">
            Invalid Reset Link
          </h2>

          <div className="bg-red-900/20 border border-red-500/50 text-red-300 px-4 py-3 rounded text-sm">
            {error || 'This password reset link is invalid or has expired.'}
          </div>

          <a
            href="/request-password-reset"
            className="block w-full text-center bg-brand-blue text-white font-semibold py-3 rounded-lg hover:bg-[#1a7aef] transition-colors"
          >
            Request New Reset Link
          </a>

          <a
            href="/login"
            className="block w-full text-center text-brand-blue hover:text-[#1a7aef] transition-colors text-sm"
          >
            Back to Login
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm bg-surface rounded-lg shadow-lg p-6 space-y-5 border border-muted"
      >
        <h2 className="text-2xl font-bold text-brand-white text-center">
          Set New Password
        </h2>

        <p className="text-brand-gray-blue text-sm text-center">
          Enter a new password for <span className="text-brand-white font-semibold">{username}</span>
        </p>

        {error && (
          <div className="bg-red-900/20 border border-red-500/50 text-red-300 px-4 py-3 rounded text-sm">
            {error}
          </div>
        )}

        <input
          type="password"
          placeholder="New password (min 6 characters)"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          className="w-full p-3 bg-gray-800 border border-brand-gray-blue rounded focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-brand-blue text-brand-white placeholder-gray-400"
          disabled={isLoading}
          required
        />

        <input
          type="password"
          placeholder="Confirm new password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className="w-full p-3 bg-gray-800 border border-brand-gray-blue rounded focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-brand-blue text-brand-white placeholder-gray-400"
          disabled={isLoading}
          required
        />

        <button
          type="submit"
          className="w-full bg-brand-blue text-white font-semibold py-3 rounded-lg hover:bg-[#1a7aef] transition-colors disabled:bg-brand-blue/50 disabled:cursor-not-allowed"
          disabled={isLoading}
        >
          {isLoading ? 'Resetting Password...' : 'Reset Password'}
        </button>
      </form>
    </div>
  );
}

export default ResetPasswordForm;
